// Central route constants for the application
// All routes are English-only and should not be localized

import { config, tutorialConfig } from './config';

/**
 * Build full URL for current origin (e.g. for window.open with _blank).
 */
export function buildFullUrl(path: string, search?: Record<string, string>): string {
  const configuredBase = config.siteUrl?.replace(/\/+$/, '') ?? '';
  const runtimeBase = typeof window !== 'undefined' ? window.location.origin : '';
  const base = configuredBase || runtimeBase;
  const pathWithLeading = path.startsWith('/') ? path : `/${path}`;
  if (!search || Object.keys(search).length === 0) return `${base}${pathWithLeading}`;
  const params = new URLSearchParams(search);
  return `${base}${pathWithLeading}?${params.toString()}`;
}

/**
 * Navigation guideline:
 * - Use TanStack Router (`Link`, `useNavigate`, route `to` + params/search) for internal navigation.
 * - Only use `window.location.*` for intentional hard reloads or truly external URLs.
 */
export const ROUTE_INDEX = '/';
export const ROUTE_404 = '/404';
export const ROUTE_DEBUG = '/debug';
export const ROUTE_RESULTS = '/results';
export const ROUTE_FREEGAME = '/freegame';

// Game replay, join, spectate: /games/:id/replay, /games/:id/join, /games/:id/spectate
export const ROUTE_GAMES = '/games';
export const buildGameReplayRoute = (gameId: string): string => `${ROUTE_GAMES}/${gameId}/replay`;
export const buildJoinRoute = (id: string): string => `${ROUTE_GAMES}/${id}/join`;
export const buildSpectateRoute = (id: string): string => `${ROUTE_GAMES}/${id}/spectate`;
export const ROUTE_FREEGAMES = '/freegames';
export const ROUTE_FREEGAMES_CREATE = '/freegames/create';
export const ROUTE_TOURNAMENTS = '/tournaments';
export const buildFreegameJoinRoute = (id: string): string => `${ROUTE_FREEGAMES}/${id}/join`;
export const buildTournamentJoinRoute = (id: string): string => `${ROUTE_TOURNAMENTS}/${id}/join`;
export const buildFreegameSpectateRoute = (id: string): string =>
  `${ROUTE_FREEGAMES}/${id}/spectate`;
export const ROUTE_SNAPSHOTS = '/snapshots';
export const buildSnapshotRoute = (id: string): string => `${ROUTE_SNAPSHOTS}/${id}`;
/** Debug page for a single snapshot (admin): /debug/:snapshotId */
export const buildDebugSnapshotRoute = (snapshotId: string): string =>
  `${ROUTE_DEBUG}/${snapshotId}`;
/** Load stored snapshot into offline training (admin): /debug/training/snapshot_<id>_<variant> */
export const buildDebugTrainingSnapshotRoute = (snapshotSlug: string): string =>
  `${ROUTE_DEBUG}/training/${snapshotSlug}`;
export const ROUTE_TABLE = '/table';
export const ROUTE_TOPLIST = '/toplist';
export const ROUTE_TOURNAMENT = '/tournament';
export const ROUTE_TRAINING = '/training';
export const ROUTE_TRAINING_CREATE = '/training/create';

// Account vs Profile canonical routes
export const ROUTE_ACCOUNT = '/account';
export const ROUTE_ACCOUNT_SUBSCRIPTIONS = '/account/subscriptions';

// Auth action pages
export const ROUTE_AUTH_ACTION = '/auth/action';

// Users (public profile): dynamic route template and builder
export const ROUTE_USERS = '/users';
export const ROUTE_USER_TEMPLATE = '/users/:id';
export const buildUserRoute = (id: string): string => `${ROUTE_USERS}/${id}`;

// Legal pages (external URLs; single source in config.tutorialConfig)
export const ROUTE_TERMS = tutorialConfig.agbs.en;
export const ROUTE_PRIVACY = tutorialConfig.privacy.en;
export const ROUTE_IMPRESSUM = tutorialConfig.impressum;

// Client-only route patterns for wildcard matching
export const ROUTE_DEBUG_PATTERN = '/debug/*';
export const ROUTE_GAMES_PATTERN = '/games/*';

/** All app URLs for debug panel (static + sample dynamic). Path is the href; label for display. */
export const DEBUG_APP_URLS: { path: string; label: string }[] = [
  { path: ROUTE_INDEX, label: 'Home (/)' },
  { path: ROUTE_404, label: '404' },
  { path: ROUTE_DEBUG, label: 'Debug' },
  { path: ROUTE_RESULTS, label: 'Results' },
  { path: ROUTE_FREEGAME, label: 'Freegame' },
  { path: ROUTE_GAMES, label: 'Games list' },
  { path: `${ROUTE_GAMES}/123/replay`, label: 'Game replay (sample)' },
  { path: `${ROUTE_GAMES}/123/join`, label: 'Game join (sample)' },
  { path: `${ROUTE_GAMES}/123/spectate`, label: 'Game spectate (sample)' },
  { path: ROUTE_FREEGAMES, label: 'Freegames (lobby)' },
  { path: ROUTE_FREEGAMES_CREATE, label: 'Freegames create' },
  { path: ROUTE_SNAPSHOTS, label: 'Snapshots list' },
  { path: `${ROUTE_SNAPSHOTS}/123`, label: 'Snapshot detail (sample)' },
  { path: ROUTE_TABLE, label: 'Table' },
  { path: ROUTE_TOPLIST, label: 'Toplist' },
  { path: ROUTE_TOURNAMENT, label: 'Tournament' },
  { path: ROUTE_TRAINING, label: 'Training' },
  { path: ROUTE_TRAINING_CREATE, label: 'Training create' },
  { path: ROUTE_ACCOUNT, label: 'Account' },
  { path: ROUTE_ACCOUNT_SUBSCRIPTIONS, label: 'Account subscriptions' },
  { path: ROUTE_AUTH_ACTION, label: 'Auth action' },
  { path: ROUTE_USERS, label: 'Users' },
  { path: `${ROUTE_USERS}/123`, label: 'User profile (sample)' },
  { path: ROUTE_TERMS, label: 'Terms (external)' },
  { path: ROUTE_PRIVACY, label: 'Privacy (external)' },
  { path: ROUTE_IMPRESSUM, label: 'Impressum (external)' },
];
